import React from 'react';
import { Helmet } from 'react-helmet-async';
import { HomePageProps } from '../components/types';
import { Section, ProductCard, DiscordButton } from '../components';
import { FeralIcon } from '../components/common/FeralIcon';
import { getPageMeta } from '../data';
import { discordLink } from '../data/links';

export const Home: React.FC<HomePageProps> = ({ products, communityLink }) => {
  const pageMeta = getPageMeta('/');

  const instruments = products.filter((p) => p.id === 'gorilla' || p.id === 'raven');
  const sequencers = products.filter((p) => p.id === 'chordpond');

  return (
    <>
      <Helmet>
        <title>{pageMeta?.title}</title>
        <meta name="description" content={pageMeta?.description} />
        <meta property="og:title" content={pageMeta?.title} />
        <meta property="og:description" content={pageMeta?.description} />
        <meta property="og:image" content={pageMeta?.ogImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageMeta?.title} />
        <meta name="twitter:description" content={pageMeta?.description} />
      </Helmet>

      {/* Hero Section - Company Branding */}
      <div className="min-h-[24vh] md:min-h-[30vh] lg:min-h-[42vh] bg-deepBlack text-white overflow-hidden relative">
        {/* Hero Section */}
        <section className="relative flex items-center justify-center overflow-hidden min-h-[24vh] md:min-h-[30vh] lg:min-h-[42vh]">
          {/* Background Gradient */}
          <div className="absolute inset-0 bg-gradient-radial from-gorillaGold/5 via-transparent to-transparent opacity-20" />

          {/* Abstract Background Element - The "Mark" (hidden on mobile, animated drift) */}
          <div className="absolute inset-0 pointer-events-none select-none overflow-hidden hidden lg:block animate-hero-drift">
            <div className="absolute top-1/2 right-[-12%] -translate-y-1/2 w-[80vh] h-[80vh] max-w-[800px] max-h-[800px]">
              <FeralIcon
                className="w-full h-full text-white"
                strokeWidth={1.5}
                style={{
                  opacity: 0.04,
                  transform: 'scale(1.45) rotate(-5deg)'
                }}
              />
            </div>
          </div>

          <div className="text-center max-w-5xl z-10 px-4 relative">
            <div className="flex justify-center mb-8">
              <FeralIcon
                className="w-[38px] h-[38px] text-white/80"
                strokeWidth={8}
              />
            </div>

            <h1 className="text-6xl md:text-8xl font-black tracking-tight text-white mb-8 font-mono">
              FERAL-AUD.IO
            </h1>

            <p className="text-lg md:text-xl text-[#CCCCCC] font-light tracking-[0.15em] uppercase max-w-[640px] mx-auto leading-loose mt-2">
              Designed for mobility.<br />Built for creation.
            </p>
          </div>

          {/* Gradient Fade */}
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-deepBlack/80 pointer-events-none" />
        </section>
      </div>

      {/* Animate drift for hero mark */}
      <style>{`
        @keyframes hero-drift {
          0% { transform: translateY(0) scale(1.45) rotate(-5deg); }
          100% { transform: translateY(-20px) scale(1.45) rotate(-3deg); }
        }
        .animate-hero-drift > div > svg {
          animation: hero-drift 24s ease-in-out infinite alternate;
        }
      `}</style>

      {/* Horizontal Rule */}
      <div className="h-px w-full bg-white/12" />

      {/* Featured Product Section */}
      <Section background="gray" id="products" className="py-16 mt-4 md:mt-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-16 border-b border-white/10 pb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-white font-mono tracking-tight">
              Instruments
            </h2>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {instruments.map((product) => (
              <div key={product.id} className="w-full h-full">
                <ProductCard product={product} />
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* Sequencers Section */}
      {sequencers.length > 0 && (
        <Section background="gray" id="sequencers" className="py-16 border-t border-white/5">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center justify-between mb-16 border-b border-white/10 pb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-white font-mono tracking-tight">
                Sequencers
              </h2>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {sequencers.map((product) => (
                <div key={product.id} className="w-full h-full">
                  <ProductCard product={product} />
                </div>
              ))}
            </div>
          </div>
        </Section>
      )}

      {/* Community Section */}
      {(communityLink || discordLink) && (
        <Section background="gray" id="community" className="border-t border-white/5">
          <div className="text-center max-w-3xl mx-auto py-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 font-mono">
              JOIN THE COMMUNITY
            </h2>
            <p className="text-lg text-gray-400 mb-10 leading-relaxed">
              Connect with other musicians and sound designers. Share presets, get help, and provide feedback directly to the developers.
            </p>
            <DiscordButton url={(communityLink || discordLink)!.url} className="px-10" />
          </div>
        </Section>
      )}
    </>
  );
};
